import * as React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Download, Plus, Trash2 } from 'lucide-react';
import {
  DEFAULT_ZONE_TIERS,
  ZONE_TIERS,
  ZONE_TIER_LABELS,
  type ZoneImportPreview,
  type ZoneTier,
} from '@aimmo/contracts';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  RelationPicker,
  resourceKeys,
  Spinner,
  toast,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { resolveComune, searchComuni } from '../resources/geo-lookups.js';

/**
 * Imports zones from the national catalogue (U6, levels added in U9).
 *
 * The preview is not decoration: importing ten comuni can be a few hundred
 * rows, and an agency should see how many, and how many it already has, before
 * anything is written. Re-importing is safe — the catalogue's own code is the
 * key — so the number that matters is "nuove".
 *
 * The levels are what keep the catalogue usable in a city. Torino's OSM rows
 * include 32 quartieri anyone would name and 41 finer areas that range from
 * Borgo Dora to "Polo Nord"; taking all 74 or none is the wrong question to ask
 * an agent. So the tiers are checkboxes, quartieri and zone ticked, microzone
 * offered — and the counts come back split by tier, so ticking one recounts
 * without another round trip.
 */
export function ZoneImportDialog() {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [comuneIds, setComuneIds] = React.useState<string[]>([]);
  const [picked, setPicked] = React.useState<string | null>(null);
  const [preview, setPreview] = React.useState<ZoneImportPreview[] | null>(null);
  const [tiers, setTiers] = React.useState<readonly ZoneTier[]>(DEFAULT_ZONE_TIERS);

  const previewMutation = useMutation({
    mutationFn: async (ids: string[]) =>
      assertOk(await api.zoneCatalog.preview({ body: { comuneIds: ids } })).body,
    onSuccess: setPreview,
  });

  const importMutation = useMutation({
    mutationFn: async (ids: string[]) =>
      assertOk(
        await api.zoneCatalog.import({
          body: { comuneIds: ids, tiers: [...tiers] as [string, ...string[]] },
        }),
      ).body,
    onSuccess: async (result) => {
      toast.success(
        result.imported === 0
          ? 'Nessuna zona nuova da importare.'
          : `${result.imported} zone importate.`,
        result.skipped > 0 ? { description: `${result.skipped} già presenti.` } : undefined,
      );
      await queryClient.invalidateQueries({ queryKey: resourceKeys.all('zones') });
      close();
    },
  });

  /**
   * Closing has to go through here rather than through `setOpen(false)`:
   * Radix calls `onOpenChange` for its own interactions — the overlay, Escape —
   * but not for a state change we make ourselves, so a manual close skipped the
   * reset and the next open inherited the last one's comuni and levels.
   */
  function close(): void {
    reset();
    setOpen(false);
  }

  function reset(): void {
    setComuneIds([]);
    setPicked(null);
    setPreview(null);
    setTiers(DEFAULT_ZONE_TIERS);
  }

  function toggleTier(tier: ZoneTier, checked: boolean): void {
    // Kept in the catalogue's own order rather than the order they were
    // ticked, so the request and the counts read the same way every time.
    setTiers(
      checked
        ? ZONE_TIERS.filter((row) => row === tier || tiers.includes(row))
        : tiers.filter((row) => row !== tier),
    );
  }

  function addComune(id: string | null): void {
    setPicked(null);
    if (!id || comuneIds.includes(id)) return;
    const next = [...comuneIds, id];
    setComuneIds(next);
    setPreview(null);
    previewMutation.mutate(next);
  }

  function removeComune(id: string): void {
    const next = comuneIds.filter((value) => value !== id);
    setComuneIds(next);
    setPreview(null);
    if (next.length > 0) previewMutation.mutate(next);
  }

  /** Only the ticked levels count towards what the button will import. */
  const totals = (preview ?? []).reduce(
    (sum, row) => {
      const chosen = row.tiers.filter((entry) => tiers.includes(entry.tier as ZoneTier));
      return {
        toImport:
          sum.toImport +
          chosen.reduce((n, entry) => n + (entry.available - entry.alreadyImported), 0),
        already: sum.already + chosen.reduce((n, entry) => n + entry.alreadyImported, 0),
      };
    },
    { toImport: 0, already: 0 },
  );

  /** One comune's figures, counting only the levels currently ticked. */
  const chosenOf = (row: ZoneImportPreview) =>
    row.tiers.filter((entry) => tiers.includes(entry.tier as ZoneTier));
  const rowToImport = (row: ZoneImportPreview) =>
    chosenOf(row).reduce((n, entry) => n + (entry.available - entry.alreadyImported), 0);
  const rowAlready = (row: ZoneImportPreview) =>
    chosenOf(row).reduce((n, entry) => n + entry.alreadyImported, 0);

  /** Per level, across every comune picked — what the checkboxes are labelled with. */
  const perTier = ZONE_TIERS.map((tier) => {
    const rows = (preview ?? []).flatMap((row) => row.tiers.filter((entry) => entry.tier === tier));
    return {
      tier,
      toImport: rows.reduce((n, entry) => n + (entry.available - entry.alreadyImported), 0),
      available: rows.reduce((n, entry) => n + entry.available, 0),
    };
  });

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <Button type="button" variant="secondary" size="sm" onClick={() => setOpen(true)}>
        <Download />
        Importa zone
      </Button>

      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Importa zone dal catalogo</DialogTitle>
          <DialogDescription>
            Quartieri e zone dei comuni scelti. Le zone già importate vengono saltate, anche se le
            hai rinominate.
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[60vh] gap-3 overflow-y-auto">
          <div className="grid gap-1">
            <span className="text-label uppercase tracking-[0.08em] text-faint">Comuni</span>
            <RelationPicker
              value={picked}
              onChange={addComune}
              search={searchComuni}
              resolve={resolveComune}
              placeholder="Cerca un comune…"
              aria-label="Aggiungi comune"
            />
          </div>

          {previewMutation.isPending && <Spinner className="text-muted" />}

          {preview && preview.length > 0 && (
            <div className="grid gap-1">
              <span className="text-label uppercase tracking-[0.08em] text-faint">Livelli</span>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {perTier.map((row) => (
                  <div key={row.tier} className="flex items-center gap-2">
                    <Checkbox
                      id={`tier-${row.tier}`}
                      checked={tiers.includes(row.tier)}
                      disabled={row.available === 0}
                      onCheckedChange={(checked) => toggleTier(row.tier, checked === true)}
                    />
                    <Label htmlFor={`tier-${row.tier}`} className="text-body text-ink">
                      {ZONE_TIER_LABELS[row.tier]}
                      <span className="text-secondary text-muted">
                        {row.available === 0 ? ' — nessuna' : ` ${row.toImport}`}
                      </span>
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          )}

          {preview && preview.length > 0 && (
            <ul className="grid gap-1">
              {preview.map((row) => (
                <li
                  key={row.comuneId}
                  className="flex flex-wrap items-center gap-2 rounded-control border border-border px-3 py-2"
                >
                  <span className="text-body text-ink">
                    {row.comuneName}
                    {row.sigla ? ` (${row.sigla})` : ''}
                  </span>
                  <Badge tone={rowToImport(row) > 0 ? 'accent' : 'neutral'}>
                    {`${rowToImport(row)} nuove`}
                  </Badge>
                  {rowAlready(row) > 0 && (
                    <span className="text-secondary text-muted">
                      {`${rowAlready(row)} già presenti`}
                    </span>
                  )}
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={`Togli ${row.comuneName}`}
                    className="ml-auto"
                    onClick={() => removeComune(row.comuneId)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
          )}

          {preview && preview.length > 0 && totals.toImport === 0 && (
            <p className="text-secondary text-muted">
              {tiers.length === 0
                ? 'Scegli almeno un livello.'
                : 'Niente da importare: le zone di questi comuni ci sono già.'}
            </p>
          )}

          {/* ODbL requires attribution wherever the derived data is shown, and
              this is the screen where it arrives (U9). Not buried in a README. */}
          <p className="text-secondary text-muted">
            Dati delle zone © <span className="text-ink">OpenStreetMap</span> contributors, sotto
            licenza ODbL.
          </p>
        </div>

        <DialogFooter>
          <Button type="button" variant="ghost" size="sm" onClick={close}>
            Annulla
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            disabled={totals.toImport === 0 || importMutation.isPending}
            onClick={() => importMutation.mutate(comuneIds)}
          >
            {importMutation.isPending && <Spinner className="text-current" />}
            <Plus />
            {`Importa ${totals.toImport} zone`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
